
// Path: /src/components/auth/components/ImgOlslm/ImgOlslm.tsx
// Updated to add showroom display with 3-5 random images, reposition smaller share buttons under modal image, and enhance SEO/accessibility

import React, { useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import { TreeSelect, Modal, Button } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet-async";
import { InlineShareButtons } from "sharethis-reactjs";
import { loginImages } from "../../constants/LoginImg";
import ImgOlslmQ1 from "./ImgOlslmQ1";
import ImgOlslmQ2 from "./ImgOlslmQ2";
import ImgOlslmQ3 from "./ImgOlslmQ3";
import ImgOlslmQ4 from "./ImgOlslmQ4";
import { useFirebase } from "../../../dev/Firebase/Firebase";
import "./ImgOlslm.css";

const { TreeNode } = TreeSelect;

// Define types for props
interface ChildQuestion {
  imageUrl: string;
  title: string;
}

interface ImgOlslmState {
  imageUrl: string;
  isImageOpen: boolean;
  isEstateOpen: boolean;
  images: string[];
  questionList: string[];
  activeQW: string;
  titleUrl: string;
  width: number;
  height: number;
  activeChildQW?: string;
  childQuestionList?: ChildQuestion[];
}

interface ImgOlslmProps {
  state: ImgOlslmState;
  onChange: (value: string) => void;
  onArrowClick: (direction: "left" | "right") => void;
  onQuestionHandler: (qw: string) => void;
  onChildQuestionHandler: (qw: string, index: number) => void;
  onChangeImageUrlHandler: (imageUrl: string) => void;
  titleUrl: string;
}

interface TreeNodeData {
  title: string;
  key: string;
  value: string;
  disabled?: boolean;
  children?: React.ReactNode;
  imageUrl?: string;
}

// Styled Components
const ImageSection = styled.div`
  position: relative;
  z-index: 5;
`;
const Container = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: 20px;
  background: transparent;
  padding: 20px;
  border-radius: 8px;
  border: none !important;
  box-shadow: none !important;
  z-index: 1;
`;

const SelectImgs = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 20px auto;
  border: none !important;
  box-shadow: none !important;
  z-index: 2;
  @media (max-width: 1040px) {
    margin-top: 10px;
  }
`;

const StyledTreeSelect = styled(TreeSelect)`
  width: 100%;
  z-index: 11;
  margin-bottom: 20px;
  .ant-select-selector {
    border-radius: 4px;
    border: none !important;
    background: #fff !important;
    font-size: 16px;
    padding: 8px;
    box-shadow: none !important;
  }
  .ant-select-tree {
    font-size: 16px;
  }
  .ant-select-tree-node-content-wrapper {
    padding: 8px;
    transition: background 0.3s ease;
  }
  .ant-select-tree-node-content-wrapper:hover {
    background: #fce248;
  }
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  width: 70%;
  margin-top: 10px;
`;

const NavIcon = styled.div`
  cursor: pointer;
  font-size: 20px;
  color: #fce248;
  &:hover {
    color: #ffaa00;
  }
`;

const LeftNavIcon = styled(ArrowLeftOutlined)`
  cursor: pointer;
  font-size: 20px;
  color: #fce248;
  &:hover {
    color: #ffaa00;
  }
`;

const RightNavIcon = styled(ArrowRightOutlined)`
  cursor: pointer;
  font-size: 20px;
  color: #fce248;
  &:hover {
    color: #ffaa00;
  }
`;

const ImageWrapper = styled.div<{ $isOpen: boolean }>`
  position: ${({ $isOpen }) => ($isOpen ? "absolute" : "relative")};
  left: ${({ $isOpen }) => ($isOpen ? "10%" : "0")};
  top: ${({ $isOpen }) => ($isOpen ? "5%" : "0")};
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: column;
  z-index: 100;
  background: #ffe082;
  padding: 20px;
  border-radius: 8px;
  border: none !important;
  box-shadow: none !important;
  @media (max-width: 768px) {
    left: 0;
    width: 90%;
  }
`;

const QuestionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
`;

const QuestionButton = styled.a<{ $bgColor: string; active: boolean }>`
  color: ${({ active }) => (active ? "#fce248" : "#fff")};
  background-color: ${({ $bgColor }) => $bgColor || "#1890ff"};
  padding: 8px 16px;
  border-radius: 4px;
  text-decoration: none;
  transform: ${({ active }) => (active ? "scale(1.1)" : "scale(1)")};
  transition: transform 0.2s ease, opacity 0.2s ease;
  border: ${({ active }) => (active ? "2px solid #fce248" : "none")};
  &:hover {
    opacity: 0.9;
  }
`;

const QuestionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
  margin-left: 20px;
  background: #ffe082;
  border: none !important;
  box-shadow: none !important;
`;

const QuestionItem = styled.a<{ active: boolean }>`
  background-color: ${({ active }) => (active ? "#fce248" : "#fff")};
  color: ${({ active }) => (active ? "#1a1a1a" : "#fce248")};
  padding: 5px 10px;
  border-radius: 4px;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid #fce248;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: #ffaa00;
    color: #fff;
  }
`;

const PartnerImage = styled.img<{ selected: boolean }>`
  margin-top: 20px;
  cursor: pointer;
  width: 150px;
  height: 150px;
  transition: transform 0.3s ease, border 0.3s ease;
  border: ${({ selected }) => (selected ? "3px solid #fce248" : "1px solid #ddd")};
  border-radius: 4px;
  z-index: 10;
  &:hover {
    transform: scale(1.05);
  }
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 15;
`;

const ShowroomDisplay = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const ShowroomImage = styled.img`
  width: 100px;
  height: 100px;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid #ddd;
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.05);
    border-color: #fce248;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const BottomShareWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 6px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  .st-btn {
    border: 1px solid #fce248 !important;
    border-radius: 4px !important;
    padding: 4px !important;
    transition: transform 0.3s ease;
    &:hover {
      transform: scale(1.05);
    }
    &:focus {
      outline: 2px solid #ffaa00;
      outline-offset: 2px;
    }
  }
  .st-total {
    color: #fce248 !important;
    font-size: 8px;
  }
  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
    padding: 6px;
    .st-btn {
      padding: 3px !important;
    }
    .st-total {
      font-size: 7px;
    }
  }
`;

const ImgOlslm: React.FC<ImgOlslmProps> = ({
  state,
  onChange,
  onArrowClick,
  onQuestionHandler,
  onChildQuestionHandler,
  onChangeImageUrlHandler,
  titleUrl,
}) => {
  const { logEvent } = useFirebase();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const isValidImage = state.imageUrl && state.imageUrl.length > 0 && state.imageUrl !== "OLSLM";

  const allImages = loginImages.images.map((img) => img.url);

  // Randomly select 3-5 images for showroom display
  const showroomImages = useMemo(() => {
    const shuffled = [...allImages].sort(() => 0.5 - Math.random());
    const count = Math.floor(Math.random() * 3) + 3; // Random 3-5
    return shuffled.slice(0, count);
  }, [allImages]);

  console.log("Verification: Length of loginImages.images:", loginImages.images.length); // Debug log

  const handleImageClick = useCallback(() => {
    const currentIndex = allImages.indexOf(state.imageUrl);
    setSelectedImageIndex(currentIndex !== -1 ? currentIndex : 0);
    setIsModalOpen(true);
    logEvent("image_click", { image_url: state.imageUrl, context: "modal_open" });
  }, [state.imageUrl, allImages, logEvent]);

  const handleShowroomImageClick = useCallback(
    (imageUrl: string) => {
      const index = allImages.indexOf(imageUrl);
      setSelectedImageIndex(index !== -1 ? index : 0);
      onChange(imageUrl);
      setIsModalOpen(true);
      logEvent("showroom_image_click", { image_url: imageUrl, context: "showroom" });
    },
    [allImages, onChange, logEvent]
  );

  const handleNextImage = useCallback(() => {
    if (selectedImageIndex !== null && selectedImageIndex < allImages.length - 1) {
      const nextIndex = selectedImageIndex + 1;
      setSelectedImageIndex(nextIndex);
      onChange(allImages[nextIndex]);
      logEvent("image_navigate", { direction: "next", image_url: allImages[nextIndex] });
    }
  }, [selectedImageIndex, allImages, onChange, logEvent]);

  const handlePrevImage = useCallback(() => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      const prevIndex = selectedImageIndex - 1;
      setSelectedImageIndex(prevIndex);
      onChange(allImages[prevIndex]);
      logEvent("image_navigate", { direction: "prev", image_url: allImages[prevIndex] });
    }
  }, [selectedImageIndex, allImages, onChange, logEvent]);

  const handleTreeExpand = useCallback((keys: string[]) => {
    setExpandedKeys(keys);
  }, []);

  const handleSelect = useCallback(
    (value: string) => {
      if (value && value.startsWith("http")) {
        const index = allImages.indexOf(value);
        setSelectedImageIndex(index !== -1 ? index : null);
        onChange(value);
      }
      if (value && !value.startsWith("http")) {
        setExpandedKeys([...expandedKeys, value]);
      }
    },
    [allImages, expandedKeys, onChange]
  );

  const treeData: TreeNodeData[] = [
    {
      title: "OLSLM",
      key: "0-1",
      value: "0-1",
      disabled: true,
      children: [
        ImgOlslmQ1({ key: "q1" }),
        ImgOlslmQ2({ key: "q2" }),
        ImgOlslmQ3({ key: "q3" }),
        ImgOlslmQ4({ key: "q4" }),
      ],
    },
  ];

  const renderTreeNodes = (data: TreeNodeData[]): React.ReactNode =>
    data.map((item) => {
      const children = Array.isArray(item.children)
        ? item.children.map((child, index) =>
            React.isValidElement(child) ? child : <TreeNode key={`child-${index}`} {...child} />
          )
        : item.children;
      return (
        <TreeNode key={item.key} value={item.value} title={item.title} disabled={item.disabled}>
          {children}
        </TreeNode>
      );
    });

  const questionButtons = [
    { id: "q1", label: "Q1", bgColor: "#1890ff" },
    { id: "q2", label: "Q2", bgColor: "#52c41a" },
    { id: "q3", label: "Q3", bgColor: "#fa8c16" },
    { id: "q4", label: "Q4", bgColor: "#722ed1" },
  ];

  return (
    <ImageSection>
      <Container>
        <Helmet>
          <title>OLS Image Selector - Sun Light Meditation</title>
          <meta
            name="description"
            content="Explore and share OLS Sun Light Meditation images with our interactive showroom. Join OLS at olsme.com/OLSStudentProspect to start your journey!"
          />
          <meta
            name="keywords"
            content="OLS images, Sun Light Meditation, onelightsystem OLS, meditation education, social sharing, OLS showroom, join OLS"
          />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: "OLS Image Selector",
              description:
                "Explore and share OLS Sun Light Meditation images with our interactive showroom.",
              url: "https://www.olsme.com",
            })}
          </script>
        </Helmet>
        <SelectImgs className="select-imgs">
          <StyledTreeSelect
            showSearch
            value={titleUrl || state.imageUrl}
            dropdownStyle={{ maxHeight: 500, overflow: "auto" }}
            placeholder="Please select an image"
            allowClear
            defaultValue="OLSLM"
            onChange={handleSelect}
            treeExpandedKeys={expandedKeys}
            onTreeExpand={handleTreeExpand}
            aria-label="Select an image from the tree"
          >
            {renderTreeNodes(treeData)}
          </StyledTreeSelect>

          {!isValidImage && (
            <ShowroomDisplay>
              {showroomImages.map((imgUrl, index) => (
                <ShowroomImage
                  key={index}
                  src={imgUrl}
                  alt={`OLS Meditation Preview ${index + 1}`}
                  onClick={() => handleShowroomImageClick(imgUrl)}
                />
              ))}
            </ShowroomDisplay>
          )}

          {isValidImage && (
            <ImageContainer>
              <PartnerImage
                onClick={handleImageClick}
                src={state.imageUrl}
                alt="OLS Meditation Image"
                selected={state.imageUrl === allImages[selectedImageIndex]}
              />
              <Modal
                open={isModalOpen}
                footer={[
                  <Button
                    key="prev"
                    onClick={handlePrevImage}
                    disabled={selectedImageIndex === 0}
                    style={{ borderColor: "#fce248", color: "#fce248" }}
                  >
                    Previous
                  </Button>,
                  <Button
                    key="next"
                    type="primary"
                    onClick={handleNextImage}
                    disabled={selectedImageIndex === allImages.length - 1}
                    style={{ background: "#fce248", borderColor: "#ffaa00" }}
                  >
                    Next
                  </Button>,
                ]}
                onCancel={() => setIsModalOpen(false)}
                width={620}
                style={{ background: "#ffe082", borderRadius: 8 }}
              >
                <ModalContent>
                  <img
                    src={allImages[selectedImageIndex] || state.imageUrl}
                    alt="Selected OLS Meditation Image"
                    style={{ width: "560px", height: "560px", borderRadius: 8 }}
                  />
                  <BottomShareWrapper>
                    <InlineShareButtons
                      config={{
                        alignment: "left",
                        color: "social",
                        enabled: true,
                        font_size: 10,
                        labels: "cta",
                        language: "en",
                        networks: [
                          "whatsapp",
                          "linkedin",
                          "messenger",
                          "facebook",
                          "twitter",
                          "blogger",
                          "pinterest",
                          "sms",
                          "email",
                        ],
                        padding: 6,
                        radius: 4,
                        show_total: true,
                        min_count: 10,
                        url: allImages[selectedImageIndex] || state.imageUrl,
                        image: allImages[selectedImageIndex] || state.imageUrl,
                        description:
                          "Discover this OLSLM image from onelightsystem OLS, inspiring Sun Light Meditation!",
                        title: "OLSLM Image - Sun Light Meditation",
                        message:
                          "Check out this OLSLM image from onelightsystem OLS!",
                        subject: "OLSLM Image from onelightsystem OLS",
                        username: "onelightsystem",
                      }}
                    />
                  </BottomShareWrapper>
                </ModalContent>
              </Modal>

              {state.isImageOpen && (
                <ImageWrapper $isOpen={state.isImageOpen}>
                  <QuestionButtons>
                    {questionButtons.map((btn) => (
                      <QuestionButton
                        key={btn.id}
                        href="/#"
                        $bgColor={btn.bgColor}
                        active={state.activeQW === btn.id}
                        onClick={() => onQuestionHandler(btn.id)}
                      >
                        {btn.label}
                      </QuestionButton>
                    ))}
                  </QuestionButtons>

                  <QuestionList>
                    {state.questionList.map((qw, index) => (
                      <QuestionItem
                        key={index}
                        active={state.activeChildQW === qw}
                        onClick={() => onChildQuestionHandler(qw, index)}
                        href="/#"
                      >
                        {qw}
                      </QuestionItem>
                    ))}
                  </QuestionList>

                  <QuestionList>
                    {(state.childQuestionList || []).map((qw, index) => (
                      <QuestionItem
                        key={index}
                        onClick={() => onChangeImageUrlHandler(qw.imageUrl)}
                        href="/#"
                        active={false}
                      >
                        {qw.title}
                      </QuestionItem>
                    ))}
                  </QuestionList>

                  <Navigation>
                    <NavIcon onClick={() => onArrowClick("left")}>
                      <LeftNavIcon />
                    </NavIcon>
                    <NavIcon onClick={() => onArrowClick("right")}>
                      <RightNavIcon />
                    </NavIcon>
                  </Navigation>
                </ImageWrapper>
              )}
            </ImageContainer>
          )}
        </SelectImgs>
      </Container>
    </ImageSection>
  );
};

export default React.memo(ImgOlslm);